import { useState } from "react";
import { format, subDays, startOfMonth, endOfMonth } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Calendar as CalendarIcon, Download, FileSpreadsheet, TrendingUp, TrendingDown, DollarSign, Package, Leaf } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useCMVReport } from "@/hooks/useCMVReport";
import { CMVChart } from "@/components/reports/CMVChart";
import { exportCMVToPDF, exportCMVToExcel } from "@/lib/cmv-export";
import { useRestaurantContext } from "@/contexts/RestaurantContext";
import { cn } from "@/lib/utils";

type DatePreset = "today" | "7days" | "30days" | "thisMonth" | "custom";

export default function CMVPage() {
  const { activeRestaurant } = useRestaurantContext();
  const [datePreset, setDatePreset] = useState<DatePreset>("30days");
  const [dateFrom, setDateFrom] = useState<Date>(subDays(new Date(), 30));
  const [dateTo, setDateTo] = useState<Date>(new Date());

  const { data: report, isLoading } = useCMVReport({ dateFrom, dateTo });

  const handlePresetChange = (preset: DatePreset) => {
    setDatePreset(preset);
    const today = new Date();

    switch (preset) {
      case "today":
        setDateFrom(today);
        setDateTo(today);
        break;
      case "7days":
        setDateFrom(subDays(today, 7));
        setDateTo(today);
        break;
      case "30days":
        setDateFrom(subDays(today, 30));
        setDateTo(today);
        break;
      case "thisMonth":
        setDateFrom(startOfMonth(today));
        setDateTo(endOfMonth(today));
        break;
    }
  };

  const handleExportPDF = () => {
    if (!report) return;
    exportCMVToPDF({
      data: report,
      dateFrom,
      dateTo,
      restaurantName: activeRestaurant?.name || "Restaurante",
    });
  };

  const handleExportExcel = () => {
    if (!report) return;
    exportCMVToExcel({
      data: report,
      dateFrom,
      dateTo,
      restaurantName: activeRestaurant?.name || "Restaurante",
    });
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value);
  };

  const formatPercentage = (value: number) => {
    return `${value.toFixed(1)}%`;
  };

  return (
    <div className="space-y-6 p-4 md:p-6">
      {/* Header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">CMV - Custo de Mercadoria Vendida</h1>
          <p className="text-sm text-muted-foreground">
            Análise de custos e margens dos produtos vendidos
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {/* Date Presets */}
          <div className="flex items-center gap-1 rounded-lg bg-muted p-1">
            {[
              { key: "today", label: "Hoje" },
              { key: "7days", label: "7 dias" },
              { key: "30days", label: "30 dias" },
              { key: "thisMonth", label: "Mês" },
            ].map((preset) => (
              <Button
                key={preset.key}
                variant={datePreset === preset.key ? "secondary" : "ghost"}
                size="sm"
                onClick={() => handlePresetChange(preset.key as DatePreset)}
                className="h-7 px-2 text-xs"
              >
                {preset.label}
              </Button>
            ))}
          </div>

          {/* Custom Date Range */}
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className={cn(
                  "h-8 justify-start text-left font-normal",
                  datePreset === "custom" && "border-primary"
                )}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {format(dateFrom, "dd/MM", { locale: ptBR })} -{" "}
                {format(dateTo, "dd/MM", { locale: ptBR })}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="end">
              <Calendar
                mode="range"
                selected={{ from: dateFrom, to: dateTo }}
                onSelect={(range) => {
                  if (range?.from) setDateFrom(range.from);
                  if (range?.to) setDateTo(range.to);
                  setDatePreset("custom");
                }}
                numberOfMonths={2}
                locale={ptBR}
              />
            </PopoverContent>
          </Popover>

          {/* Export Buttons */}
          <Button variant="outline" size="sm" onClick={handleExportPDF} disabled={isLoading}>
            <Download className="mr-2 h-4 w-4" />
            PDF
          </Button>
          <Button variant="outline" size="sm" onClick={handleExportExcel} disabled={isLoading}>
            <FileSpreadsheet className="mr-2 h-4 w-4" />
            Excel
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Receita Total
            </CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <Skeleton className="h-8 w-24" />
            ) : (
              <p className="text-2xl font-bold">{formatCurrency(report?.totalRevenue || 0)}</p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Custo Total (CMV)
            </CardTitle>
            <Package className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <Skeleton className="h-8 w-24" />
            ) : (
              <p className="text-2xl font-bold text-destructive">
                {formatCurrency(report?.totalCost || 0)}
              </p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Margem Bruta
            </CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <Skeleton className="h-8 w-24" />
            ) : (
              <p className="text-2xl font-bold text-green-600">
                {formatCurrency(report?.totalMargin || 0)}
              </p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              % Margem
            </CardTitle>
            {(report?.marginPercentage || 0) >= 50 ? (
              <TrendingUp className="h-4 w-4 text-green-600" />
            ) : (
              <TrendingDown className="h-4 w-4 text-amber-600" />
            )}
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <Skeleton className="h-8 w-24" />
            ) : (
              <p
                className={cn(
                  "text-2xl font-bold",
                  (report?.marginPercentage || 0) >= 50 ? "text-green-600" : "text-amber-600"
                )}
              >
                {formatPercentage(report?.marginPercentage || 0)}
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* CMV Chart */}
      <Card>
        <CardHeader>
          <CardTitle>Evolução do CMV</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <Skeleton className="h-[300px] w-full" />
          ) : (
            <CMVChart data={report?.dailyCMV || []} />
          )}
        </CardContent>
      </Card>

      {/* Products Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Package className="h-5 w-5" />
            CMV por Produto
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-2">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Produto</TableHead>
                  <TableHead className="text-right">Qtd Vendida</TableHead>
                  <TableHead className="text-right">Receita</TableHead>
                  <TableHead className="text-right">Custo</TableHead>
                  <TableHead className="text-right">Margem</TableHead>
                  <TableHead className="text-right">% Margem</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(report?.productsCMV || []).map((product) => (
                  <TableRow key={product.productId}>
                    <TableCell className="font-medium">{product.productName}</TableCell>
                    <TableCell className="text-right">{product.quantitySold}</TableCell>
                    <TableCell className="text-right">{formatCurrency(product.totalRevenue)}</TableCell>
                    <TableCell className="text-right text-destructive">
                      {formatCurrency(product.totalCost)}
                    </TableCell>
                    <TableCell className="text-right text-green-600">
                      {formatCurrency(product.margin)}
                    </TableCell>
                    <TableCell
                      className={cn(
                        "text-right font-medium",
                        product.marginPercentage >= 50 ? "text-green-600" : "text-amber-600"
                      )}
                    >
                      {formatPercentage(product.marginPercentage)}
                    </TableCell>
                  </TableRow>
                ))}
                {(report?.productsCMV || []).length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                      Nenhum produto vendido no período
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Ingredients Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Leaf className="h-5 w-5" />
            Consumo de Ingredientes
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-2">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Ingrediente</TableHead>
                  <TableHead className="text-right">Qtd Consumida</TableHead>
                  <TableHead className="text-right">Unidade</TableHead>
                  <TableHead className="text-right">Custo Médio</TableHead>
                  <TableHead className="text-right">Custo Total</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(report?.ingredientConsumption || []).map((ingredient) => (
                  <TableRow key={ingredient.ingredientId}>
                    <TableCell className="font-medium">{ingredient.ingredientName}</TableCell>
                    <TableCell className="text-right">{ingredient.quantityConsumed.toFixed(2)}</TableCell>
                    <TableCell className="text-right">{ingredient.unit}</TableCell>
                    <TableCell className="text-right">{formatCurrency(ingredient.averageCost)}</TableCell>
                    <TableCell className="text-right text-destructive">
                      {formatCurrency(ingredient.totalCost)}
                    </TableCell>
                  </TableRow>
                ))}
                {(report?.ingredientConsumption || []).length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                      Nenhum ingrediente consumido no período
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
